import React, { useState, useEffect, useRef } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';

const Register = ({ onBack, onSuccess }) => {
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [successAnim, setSuccessAnim] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const successTimeout = useRef();

  useEffect(() => {
    if (registerSuccess) {
      successTimeout.current = setTimeout(() => setShowSuccess(true), 50);
    } else {
      setShowSuccess(false);
    }
    return () => clearTimeout(successTimeout.current);
  }, [registerSuccess]);

  const handleRegisterChange = (e) => {
    setRegisterData({
      ...registerData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');
    setRegisterSuccess('');
    setLoading(true);
    setSuccessAnim(false);
    const startTime = Date.now();

    if (registerData.password !== registerData.confirmPassword) {
      setRegisterError('As senhas não coincidem.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_AUTHAPI}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: registerData.email,
          email: registerData.email,
          firstName: registerData.firstName || '',
          lastName: registerData.lastName || '',
          enabled: true,
          credentials: [
            {
              type: 'password',
              value: registerData.password,
              temporary: false
            }
          ]
        })
      });

      if (!response.ok) {
        let data = {};
        try {
          data = await response.json();
        } catch (_) {
          // fallback
        }
        if (response.status === 409 || (data?.detail && data.detail.toLowerCase().includes('já possui cadastro'))) {
          setRegisterError(
            'O usuário com o e-mail declarado já possui cadastro no sistema. Tente logar ou redefinir a senha!'
          );
        } else if (data?.detail) {
          setRegisterError(data.detail);
        } else if (data?.message) {
          setRegisterError(data.message);
        } else {
          setRegisterError('Erro ao cadastrar usuário.');
        }
        setLoading(false);
        return;
      }

      // Mensagem de sucesso com destaque
      setRegisterSuccess(
        <span>
          <strong>Bem-vindo, {registerData.firstName}{registerData.lastName ? ' ' + registerData.lastName : ''}!</strong><br />
          <strong>Enviamos um código de verificação para o seu e-mail.</strong><br />
          Por favor, verifique sua caixa de entrada e siga as instruções para concluir seu cadastro!
        </span>
      );
      setFieldsDisabled(true);

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(500 - elapsedTime, 0);
      setTimeout(() => {
        setSuccessAnim(true);
        setLoading(false);
        setTimeout(() => {
          setSuccessAnim(false);
        }, 1200);
      }, remainingTime);
    } catch (err) {
      setRegisterError('Erro de conexão com o servidor.');
      setLoading(false);
    }
  };

  // Estilo para inputs desabilitados
  const disabledInputStyle = fieldsDisabled
    ? { background: '#f1f1f1', color: '#888', cursor: 'not-allowed' }
    : {};

  return (
    <form onSubmit={handleRegisterSubmit} style={{ marginTop: '-30px' }}>
      <div className="back-arrow">
        <ArrowBackIcon onClick={onBack} style={{ cursor: 'pointer' }} />
      </div>
      <h2>Realize seu Cadastro</h2>
      <div className='ns-form-container'>
        <div className="ns-form-group">
          <div className="input-group">
            <input
              type="text"
              name="firstName"
              placeholder="Nome"
              value={registerData.firstName}
              onChange={handleRegisterChange}
              required
              disabled={fieldsDisabled}
              style={disabledInputStyle}
            />
          </div>
          <div className="input-group">
            <input
              type="text"
              name="lastName"
              placeholder="Sobrenome"
              value={registerData.lastName}
              onChange={handleRegisterChange}
              required
              disabled={fieldsDisabled}
              style={disabledInputStyle}
            />
          </div>
        </div>
      </div>
      <div className="input-group">
        <input
          type="email"
          name="email"
          placeholder="E-mail"
          value={registerData.email}
          onChange={handleRegisterChange}
          required
          disabled={fieldsDisabled}
          style={disabledInputStyle}
        />
      </div>
      <div className="input-group">
        <input
          type="password"
          name="password"
          placeholder="Senha"
          value={registerData.password}
          onChange={handleRegisterChange}
          required
          disabled={fieldsDisabled}
          style={disabledInputStyle}
        />
      </div>
      <div className="input-group">
        <input
          type="password"
          name="confirmPassword"
          placeholder="Confirmação de Senha"
          value={registerData.confirmPassword}
          onChange={handleRegisterChange}
          required
          disabled={fieldsDisabled}
          style={disabledInputStyle}
        />
      </div>
      <div className="Cadastro" style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          type="submit"
          className={`sign-in ${loading ? 'loading' : ''} ${successAnim ? 'success' : ''}`}
          disabled={loading || successAnim || fieldsDisabled}
          style={{ position: 'relative', minWidth: 140 }}
        >
          {loading && (
            <span className="spinner" style={{
              width: 20, height: 20, border: '3px solid #b7e4c7', borderTop: '3px solid #218838',
              borderRadius: '50%', display: 'inline-block', marginRight: 8, verticalAlign: 'middle',
              animation: 'spin 1s linear infinite'
            }} />
          )}
          {successAnim && (
            <CheckIcon style={{ color: "#fff", marginRight: 8, verticalAlign: 'middle' }} />
          )}
          {successAnim
            ? 'Cadastrado!'
            : loading
              ? 'Cadastrando...'
              : 'Cadastrar'}
        </button>
      </div>
      {/* Mensagem de sucesso com transição suave */}
      <div
        className={`success-message animated-success-message${showSuccess ? ' show' : ''}`}
        style={{ minHeight: registerSuccess ? 0 : undefined }}
      >
        {registerSuccess}
      </div>
      {/* Mensagem de erro */}
      {registerError && (
        <p className="error-message">{registerError}</p>
      )}
    </form>
  );
};

export default Register;
